import { Box, Button, MenuItem, TextField } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { isDarkMode, themeColors } from '../../utils/constants';
import { gqlFetch, textFieldStyles } from './XTwitterShared';

let chartRegistered = false;
if (!chartRegistered) {
  Chart.register(...registerables);
  chartRegistered = true;
}

const SPORTS_DATA_CARDS_QUERY = `
  query GetSportsDataCards($league: String, $teamId: ID, $playerId: ID) {
    admin {
      getSportsDataCards(league: $league, teamId: $teamId, playerId: $playerId)
    }
  }
`;

const SUPPORTED_LEAGUES = ['MLB', 'NBA'];

const palette = {
  text: themeColors.color,
  muted: isDarkMode ? 'rgba(246,246,246,0.66)' : 'rgba(29,29,31,0.62)',
  border: isDarkMode ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)',
  panel: isDarkMode ? 'rgba(18,18,20,0.84)' : 'rgba(255,255,255,0.96)',
  surface: isDarkMode ? 'rgba(10,10,11,0.62)' : 'rgba(248,249,252,0.96)',
};

const TeamLogo = ({ team, size = 22 }) => {
  const dimension = typeof size === 'number' ? `${size}px` : size;
  if (team?.logo_url) {
    return (
      <Box
        component="img"
        src={team.logo_url}
        alt=""
        sx={{ width: dimension, height: dimension, objectFit: 'contain', flexShrink: 0 }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: dimension,
        height: dimension,
        borderRadius: '50%',
        border: `1px solid ${palette.border}`,
        backgroundColor: palette.surface,
        color: palette.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '10px',
        fontWeight: 950,
        flexShrink: 0,
      }}
    >
      {team?.abbreviation || 'TBD'}
    </Box>
  );
};

const scrollAreaSx = {
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  maxWidth: '100%',
};

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const statValue = (value) => {
  if (value == null || value === '') return 'N/A';
  return String(value);
};

const hasStatValue = (value) => value != null && value !== '';

const hasAnyStat = (stats, keys) => {
  if (!stats) return false;
  return keys.some((key) => hasStatValue(stats[key]));
};

const BASEBALL_PITCHING_POSITIONS = new Set(['P', 'SP', 'RP', 'CP', 'CL']);

const playerPositionTokens = (player) => (
  String(player?.position || player?.primary_position || '')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean)
);

const baseballPlayerStatModes = (player) => {
  const positions = playerPositionTokens(player);
  if (positions.length === 0) return { hitting: true, pitching: true };
  if (positions.includes('TWP')) return { hitting: true, pitching: true };

  const hasPitchingPosition = positions.some((position) => BASEBALL_PITCHING_POSITIONS.has(position));
  const hasNonPitchingPosition = positions.some((position) => !BASEBALL_PITCHING_POSITIONS.has(position));
  if (hasPitchingPosition && !hasNonPitchingPosition) return { hitting: false, pitching: true };
  if (hasPitchingPosition && hasNonPitchingPosition) return { hitting: true, pitching: true };

  return { hitting: true, pitching: false };
};

const dateLabel = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
};

const comparisonColors = {
  better: {
    color: isDarkMode ? '#7ee2a8' : '#047857',
    background: isDarkMode ? 'rgba(34,197,94,0.13)' : 'rgba(16,185,129,0.1)',
    border: isDarkMode ? 'rgba(74,222,128,0.34)' : 'rgba(5,150,105,0.18)',
  },
  worse: {
    color: isDarkMode ? '#fca5a5' : '#b91c1c',
    background: isDarkMode ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.08)',
    border: isDarkMode ? 'rgba(248,113,113,0.3)' : 'rgba(220,38,38,0.16)',
  },
};

const numericComparisonValue = (value, type = 'number') => {
  if (value == null || value === '') return null;
  if (type === 'record') {
    const match = String(value).match(/(\d+)\s*-\s*(\d+)/);
    if (!match) return null;
    const wins = Number(match[1]);
    const losses = Number(match[2]);
    const total = wins + losses;
    return total > 0 ? wins / total : null;
  }
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const comparisonSide = (value, opponentValue, direction = 'higher', type = 'number') => {
  const current = numericComparisonValue(value, type);
  const opponent = numericComparisonValue(opponentValue, type);
  if (current == null || opponent == null || current === opponent) return 'neutral';

  const currentIsBetter = direction === 'lower' ? current < opponent : current > opponent;
  return currentIsBetter ? 'better' : 'worse';
};

const readableColor = (hex) => {
  const color = String(hex || '').replace('#', '');
  if (color.length !== 6) return '#ffffff';
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);
  return ((r * 299 + g * 587 + b * 114) / 1000) > 145 ? '#111111' : '#ffffff';
};

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      border: `1px solid ${palette.border}`,
      borderRadius: '8px',
      backgroundColor: palette.panel,
      p: { xs: 1.25, sm: 2 },
      minWidth: 0,
      maxWidth: '100%',
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Metric = ({ label, value, compact = false }) => (
  <Box sx={{ minWidth: { xs: 0, sm: 92 } }}>
    <Box sx={{ color: palette.muted, fontSize: compact ? { xs: '10px', sm: '11px' } : '11px', fontWeight: 750, textTransform: 'uppercase' }}>{label}</Box>
    <Box sx={{ color: palette.text, fontSize: compact ? { xs: '17px', sm: '22px' } : { xs: '18px', sm: '22px' }, fontWeight: 900, lineHeight: 1.1, overflowWrap: 'anywhere' }}>{statValue(value)}</Box>
  </Box>
);

const StatGrid = ({ stats, mobileColumns = 2 }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: `repeat(${mobileColumns}, minmax(0, 1fr))`,
        sm: 'repeat(auto-fit, minmax(88px, 1fr))',
      },
      columnGap: { xs: mobileColumns >= 4 ? 0.75 : 1, sm: 1.2 },
      rowGap: { xs: 1, sm: 1.2 },
    }}
  >
    {stats.map((stat) => <Metric key={stat.label} label={stat.label} value={stat.value} compact={mobileColumns >= 4} />)}
  </Box>
);

const ComparisonValue = ({ value, side, align = 'left' }) => {
  const colors = comparisonColors[side];
  return (
    <Box sx={{ display: 'flex', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
      <Box
        component="span"
        sx={{
          color: colors?.color || palette.text,
          backgroundColor: colors?.background || 'transparent',
          border: `1px solid ${colors?.border || 'transparent'}`,
          borderRadius: '999px',
          px: 0.75,
          py: 0.18,
          minWidth: { xs: 34, sm: 42 },
          textAlign: 'center',
          fontSize: { xs: '14px', sm: '15px' },
          fontWeight: 900,
          lineHeight: 1.35,
        }}
      >
        {statValue(value)}
      </Box>
    </Box>
  );
};

const StartingLineupTable = ({ lineup }) => {
  const players = Array.isArray(lineup?.players) ? lineup.players : [];
  if (players.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline', flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900 }}>Starting Lineup</Box>
        <Box sx={{ color: palette.muted, fontSize: '11px', fontWeight: 750 }}>
          {lineup.event_label} · {dateLabel(lineup.starts_at)} · {lineup.lineup_type || 'lineup'}
        </Box>
      </Box>
      <Box sx={{ ...scrollAreaSx, border: `1px solid ${palette.border}`, borderRadius: '8px', backgroundColor: palette.surface }}>
        <Box component="table" sx={{ width: { xs: 'auto', sm: '100%' }, minWidth: { xs: 430, sm: 560 }, borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ borderBottom: `1px solid ${palette.border}` }}>
              {['#', 'Player', 'Pos', 'H-AB', 'HR', 'RBI', 'SB', 'AVG'].map((header) => (
                <Box
                  key={header}
                  component="th"
                  sx={{
                    color: palette.muted,
                    textAlign: header === 'Player' ? 'left' : 'right',
                    fontSize: '11px',
                    fontWeight: 850,
                    p: 0.75,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    width: header === '#' ? { xs: 30, sm: 'auto' } : header === 'Player' ? { xs: 132, sm: 'auto' } : { xs: 42, sm: 'auto' },
                  }}
                >
                  {header}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {players.map((player, index) => (
              <Box key={`${player.id}-${player.batting_order || index}`} component="tr" sx={{ borderBottom: `1px solid ${palette.border}`, '&:last-child': { borderBottom: 0 } }}>
                <Box component="td" sx={{ color: palette.muted, p: 0.75, textAlign: 'right', fontSize: '12px', fontWeight: 850 }}>{player.batting_order || index + 1}</Box>
                <Box component="td" sx={{ p: 0.75, width: { xs: 132, sm: 'auto' }, maxWidth: { xs: 132, sm: 'none' }, minWidth: { sm: 180 } }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ color: palette.text, fontSize: '12px', fontWeight: 850, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{player.name}</Box>
                    <Box sx={{ color: palette.muted, fontSize: '10px', mt: 0.2 }}>Bats {player.bats || 'N/A'}</Box>
                  </Box>
                </Box>
                <Box component="td" sx={{ color: palette.text, p: 0.75, textAlign: 'right', fontSize: '12px', fontWeight: 800, width: { xs: 42, sm: 'auto' }, whiteSpace: 'nowrap' }}>{statValue(player.position)}</Box>
                {['h_ab', 'hr', 'rbi', 'sb', 'avg'].map((key) => (
                  <Box key={key} component="td" sx={{ color: palette.text, p: 0.75, textAlign: 'right', fontSize: '12px', fontWeight: 850, width: { xs: 42, sm: 'auto' }, whiteSpace: 'nowrap' }}>{statValue(player.stats?.[key])}</Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const LastGamesTable = ({ games }) => {
  const rows = Array.isArray(games) ? games : [];
  if (rows.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900, mb: 1 }}>Last 10 Games</Box>
      <Box sx={{ ...scrollAreaSx, border: `1px solid ${palette.border}`, borderRadius: '8px', backgroundColor: palette.surface }}>
        <Box component="table" sx={{ width: '100%', minWidth: { xs: 360, sm: 420 }, borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ borderBottom: `1px solid ${palette.border}` }}>
              {['Date', 'Opponent', 'Result'].map((header) => (
                <Box key={header} component="th" sx={{ color: palette.muted, textAlign: header === 'Result' ? 'right' : 'left', fontSize: '11px', fontWeight: 850, p: 0.75, textTransform: 'uppercase' }}>{header}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((game) => {
              const resultColor = game.result === 'W' ? '#2e7d32' : game.result === 'L' ? '#d32f2f' : palette.muted;
              return (
                <Box key={game.event_id} component="tr" sx={{ borderBottom: `1px solid ${palette.border}`, '&:last-child': { borderBottom: 0 } }}>
                  <Box component="td" sx={{ color: palette.text, p: 0.75, fontSize: '12px', fontWeight: 750 }}>{dateLabel(game.date)}</Box>
                  <Box component="td" sx={{ p: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <TeamLogo team={game.opponent} size={22} />
                      <Box sx={{ color: palette.text, fontSize: '12px', fontWeight: 850 }}>{game.opponent_label}</Box>
                    </Box>
                  </Box>
                  <Box component="td" sx={{ p: 0.75, textAlign: 'right', fontSize: '12px', fontWeight: 950, color: resultColor }}>
                    {game.result} {game.score}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const TeamLeaders = ({ leaders }) => {
  const sections = Array.isArray(leaders?.sections) ? leaders.sections : null;
  const hitting = Array.isArray(leaders?.hitting) ? leaders.hitting : [];
  const pitching = Array.isArray(leaders?.pitching) ? leaders.pitching : [];
  if (!sections && hitting.length === 0 && pitching.length === 0) return null;

  const renderLeader = (leader) => (
    <Box
      key={leader.label}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 1,
        alignItems: 'center',
        py: 0.65,
        borderBottom: `1px solid ${palette.border}`,
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ color: palette.muted, fontSize: '11px', fontWeight: 850, textTransform: 'uppercase' }}>{leader.label}</Box>
        <Box sx={{ color: palette.text, fontSize: '12px', fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {leader.player?.name || 'N/A'}
        </Box>
      </Box>
      <Box sx={{ color: palette.text, fontSize: '16px', fontWeight: 950, textAlign: 'right' }}>
        {statValue(leader.per_game || leader.value)}
      </Box>
    </Box>
  );
  const renderedSections = sections || [
    { title: 'Hitting', rows: hitting },
    { title: 'Pitching', rows: pitching },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900, mb: 1 }}>Team Leaders</Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.2 }}>
        {renderedSections.map((section) => (
          <Box key={section.title} sx={{ border: `1px solid ${palette.border}`, borderRadius: '8px', backgroundColor: palette.surface, p: 1 }}>
            <Box sx={{ color: palette.text, fontSize: '12px', fontWeight: 900, mb: 0.4 }}>{section.title}</Box>
            {(section.rows || []).map(renderLeader)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const TeamHeader = ({ team }) => {
  const primary = team?.primary_color || '#005a9c';
  const color = readableColor(primary);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        textAlign: { xs: 'center', sm: 'left' },
        gap: 1.5,
        borderRadius: '8px',
        p: { xs: 1.5, sm: 2 },
        background: `linear-gradient(135deg, ${primary}, ${team?.alternate_color || '#ffffff'})`,
        color,
      }}
    >
      <TeamLogo team={team} size={{ xs: 58, sm: 72 }} />
      <Box>
        <Box sx={{ fontSize: { xs: '22px', sm: '26px' }, fontWeight: 950, lineHeight: 1 }}>{team?.abbreviation || 'MLB'}</Box>
        <Box sx={{ fontSize: '14px', fontWeight: 800, mt: 0.5 }}>{team?.name}</Box>
        {team?.record && <Box sx={{ fontSize: '12px', fontWeight: 750, mt: 0.4 }}>Record {team.record}</Box>}
      </Box>
    </Box>
  );
};

const PlayerStatSection = ({ title, subtitle, hitting, pitching, mobileColumns = 2 }) => {
  const groups = [];
  if (hasAnyStat(hitting, ['avg', 'hr', 'rbi', 'ops'])) {
    groups.push({
      title: 'Hitting',
      stats: [
        { label: 'AVG', value: hitting?.avg },
        { label: 'HR', value: hitting?.hr },
        { label: 'RBI', value: hitting?.rbi },
        { label: 'OPS', value: hitting?.ops },
      ],
    });
  }
  if (hasAnyStat(pitching, ['record', 'era', 'k', 'whip'])) {
    groups.push({
      title: 'Pitching',
      stats: [
        { label: 'W-L', value: pitching?.record },
        { label: 'ERA', value: pitching?.era },
        { label: 'K', value: pitching?.k },
        { label: 'WHIP', value: pitching?.whip },
      ],
    });
  }
  if (groups.length === 0) return null;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'baseline' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900 }}>{title}</Box>
        {subtitle && <Box sx={{ color: palette.muted, fontSize: '11px', fontWeight: 750 }}>{subtitle}</Box>}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: groups.length > 1 ? '1fr 1fr' : '1fr' }, gap: 1.5 }}>
        {groups.map((group) => (
          <Box key={group.title} sx={{ border: `1px solid ${palette.border}`, borderRadius: '8px', backgroundColor: palette.surface, p: 1.2 }}>
            <Box sx={{ color: palette.text, fontSize: '12px', fontWeight: 900, mb: 0.8 }}>{group.title}</Box>
            <StatGrid stats={group.stats} mobileColumns={mobileColumns} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const PlayerCard = ({ player, card, team }) => {
  const sportKey = card?.sport_key;
  const hitting = card?.hitting || {};
  const pitching = card?.pitching || {};
  const basketball = card?.basketball || {};
  const seasonStats = card?.season_stats || {};
  const recentGames = Array.isArray(card?.recent_games) ? card.recent_games : [];
  const recentHittingStats = [
    { label: 'Games', value: hitting.G },
    { label: 'AVG', value: hitting.AVG },
    { label: 'Hits', value: hitting.H },
    { label: 'HR', value: hitting.HR },
    { label: 'RBI', value: hitting.RBI },
    { label: 'BB', value: hitting.BB },
    { label: 'SO', value: hitting.SO },
    { label: 'TB', value: hitting.TB },
  ];
  const recentPitchingStats = [
    { label: 'IP', value: pitching.IP },
    { label: 'ERA', value: pitching.ERA },
    { label: 'SO', value: pitching.SO },
    { label: 'BB', value: pitching.BB },
    { label: 'ER', value: pitching.ER },
    { label: 'Pitches', value: pitching.Pitches },
  ];
  const statModes = baseballPlayerStatModes(player);
  const showRecentHitting = statModes.hitting && hasAnyStat(hitting, ['G', 'AVG', 'H', 'HR', 'RBI', 'BB', 'SO', 'TB']);
  const showRecentPitching = statModes.pitching && hasAnyStat(pitching, ['IP', 'ERA', 'SO', 'BB', 'ER', 'Pitches']);
  const recentHeaders = [
    'Date',
    'Game',
    ...(showRecentHitting ? ['AB', 'H', 'R', 'RBI', 'HR', 'BB', 'SO'] : []),
    ...(showRecentPitching ? ['IP', 'P SO'] : []),
  ];

  if (sportKey === 'basketball') {
    const seasonBasketball = seasonStats.basketball || {};
    const basketballStats = [
      { label: 'Games', value: seasonBasketball.G || basketball.G },
      { label: 'PTS', value: seasonBasketball.PTS || basketball.PTS },
      { label: 'REB', value: seasonBasketball.REB || basketball.REB },
      { label: 'AST', value: seasonBasketball.AST || basketball.AST },
      { label: 'STL', value: seasonBasketball.STL || basketball.STL },
      { label: 'BLK', value: seasonBasketball.BLK || basketball.BLK },
      { label: 'TOV', value: seasonBasketball.TOV || basketball.TOV },
      { label: 'FG%', value: seasonBasketball['FG%'] || basketball['FG%'] },
      { label: '3P%', value: seasonBasketball['3P%'] || basketball['3P%'] },
      { label: '+/-', value: seasonBasketball['+/-'] || basketball['+/-'] },
    ];

    return (
      <Card sx={{ gridColumn: { lg: 'span 2' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1.2, sm: 2 }, alignItems: 'center', mb: 2 }}>
          {player?.headshot_url && (
            <Box component="img" src={player.headshot_url} alt="" sx={{ width: { xs: 84, sm: 112 }, height: { xs: 84, sm: 112 }, borderRadius: '8px', objectFit: 'cover', backgroundColor: palette.surface }} />
          )}
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Box sx={{ color: palette.text, fontSize: { xs: '22px', sm: '26px' }, fontWeight: 950, lineHeight: 1.05 }}>{player?.full_name || player?.name}</Box>
            <Box sx={{ color: palette.muted, fontSize: '13px', mt: 0.7 }}>
              {team?.abbreviation} · {player?.position || 'N/A'}
            </Box>
          </Box>
        </Box>

        <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900, mb: 1 }}>Season Stats</Box>
        <StatGrid stats={basketballStats} />

        <Box sx={{ mt: 2, ...scrollAreaSx }}>
          <Box component="table" sx={{ width: '100%', minWidth: { xs: 560, sm: 640 }, borderCollapse: 'collapse' }}>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: `1px solid ${palette.border}` }}>
                {['Date', 'Game', 'MIN', 'PTS', 'REB', 'AST', 'STL', 'BLK', '3PM', '+/-'].map((header) => (
                  <Box key={header} component="th" sx={{ color: palette.muted, textAlign: 'left', fontSize: '11px', fontWeight: 850, p: 0.8 }}>{header}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {recentGames.map((game) => (
                <Box key={game.event_id} component="tr" sx={{ borderBottom: `1px solid ${palette.border}` }}>
                  <Box component="td" sx={{ p: 0.8, fontSize: '12px' }}>{dateLabel(game.date)}</Box>
                  <Box component="td" sx={{ p: 0.8, fontSize: '12px', fontWeight: 750 }}>{game.matchup}</Box>
                  {['mins', 'pts', 'reb', 'ast', 'stl', 'blk', 'tptfgm', 'plusMinus'].map((key) => (
                    <Box key={key} component="td" sx={{ p: 0.8, fontSize: '12px' }}>{statValue(game.basketball?.[key])}</Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ gridColumn: { lg: 'span 2' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1.2, sm: 2 }, alignItems: 'center', mb: 2 }}>
        {player?.headshot_url && (
          <Box component="img" src={player.headshot_url} alt="" sx={{ width: { xs: 84, sm: 112 }, height: { xs: 84, sm: 112 }, borderRadius: '8px', objectFit: 'cover', backgroundColor: palette.surface }} />
        )}
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <Box sx={{ color: palette.text, fontSize: { xs: '22px', sm: '26px' }, fontWeight: 950, lineHeight: 1.05 }}>{player?.full_name || player?.name}</Box>
          <Box sx={{ color: palette.muted, fontSize: '13px', mt: 0.7 }}>
            {team?.abbreviation} · {player?.position || 'N/A'} · Bats {player?.bats || 'N/A'} · Throws {player?.throws || 'N/A'}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        <PlayerStatSection
          title="Season Stats"
          hitting={statModes.hitting ? seasonStats.hitting : null}
          pitching={statModes.pitching ? seasonStats.pitching : null}
          mobileColumns={4}
        />
      </Box>

      {(showRecentHitting || showRecentPitching) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showRecentHitting && showRecentPitching ? '1fr 1fr' : '1fr' }, gap: 2 }}>
          {showRecentHitting && (
            <Box>
              <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900, mb: 1 }}>Recent Hitting</Box>
              <StatGrid stats={recentHittingStats} mobileColumns={4} />
            </Box>
          )}
          {showRecentPitching && (
            <Box>
              <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900, mb: 1 }}>Recent Pitching</Box>
              <StatGrid stats={recentPitchingStats} mobileColumns={4} />
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mt: 2, ...scrollAreaSx }}>
        <Box component="table" sx={{ width: '100%', minWidth: { xs: 560, sm: 640 }, borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ borderBottom: `1px solid ${palette.border}` }}>
              {recentHeaders.map((header) => (
                <Box key={header} component="th" sx={{ color: palette.muted, textAlign: 'left', fontSize: '11px', fontWeight: 850, p: 0.8 }}>{header}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {recentGames.map((game) => (
              <Box key={game.event_id} component="tr" sx={{ borderBottom: `1px solid ${palette.border}` }}>
                <Box component="td" sx={{ p: 0.8, fontSize: '12px' }}>{dateLabel(game.date)}</Box>
                <Box component="td" sx={{ p: 0.8, fontSize: '12px', fontWeight: 750 }}>{game.matchup}</Box>
                {showRecentHitting && ['AB', 'H', 'R', 'RBI', 'HR', 'BB', 'SO'].map((key) => (
                  <Box key={key} component="td" sx={{ p: 0.8, fontSize: '12px' }}>{statValue(game.hitting?.[key])}</Box>
                ))}
                {showRecentPitching && (
                  <>
                    <Box component="td" sx={{ p: 0.8, fontSize: '12px' }}>{statValue(game.pitching?.InningsPitched)}</Box>
                    <Box component="td" sx={{ p: 0.8, fontSize: '12px' }}>{statValue(game.pitching?.SO)}</Box>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const TeamCard = ({ team, card }) => {
  const sportKey = card?.sport_key;
  const overall = card?.overall || {};
  const hitting = card?.hitting || {};
  const pitching = card?.pitching || {};
  const basketball = card?.basketball || {};
  const startingLineup = card?.starting_lineup;
  const lastGames = card?.last_10_games;
  const leaders = card?.leaders;
  const recordTimeline = Array.isArray(card?.record_timeline) ? card.record_timeline : [];
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    const teamColor = team?.primary_color || '#005a9c';
    const labels = recordTimeline.map((point) => dateLabel(point.date || point.starts_at));

    chartInstanceRef.current = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Wins',
            data: recordTimeline.map((point) => numberValue(point.wins)),
            borderColor: teamColor,
            backgroundColor: teamColor,
            pointRadius: 0,
            pointHoverRadius: 4,
            borderWidth: 2,
            tension: 0.2,
          },
          {
            label: 'Losses',
            data: recordTimeline.map((point) => numberValue(point.losses)),
            borderColor: '#d32f2f',
            backgroundColor: '#d32f2f',
            pointRadius: 0,
            pointHoverRadius: 4,
            borderWidth: 2,
            tension: 0.2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { labels: { color: palette.text, boxWidth: 12, boxHeight: 12 } },
          tooltip: {
            callbacks: {
              title: (items) => items?.[0]?.label || '',
              label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
              afterBody: (items) => {
                const point = recordTimeline[items?.[0]?.dataIndex];
                return point ? `Record: ${point.record}` : '';
              },
            },
          },
        },
        scales: {
          x: {
            ticks: { color: palette.muted, maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
            grid: { color: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
          },
          y: {
            beginAtZero: true,
            ticks: { color: palette.muted, precision: 0 },
            grid: { color: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
          },
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    };
  }, [recordTimeline, team?.primary_color]);

  const teamStats = sportKey === 'basketball' ? [
    { label: 'Record', value: team?.record },
    { label: 'PPG', value: basketball.ppg },
    { label: 'Opp PPG', value: basketball.opponent_ppg },
    { label: 'Diff', value: basketball.point_diff },
    { label: 'AST', value: basketball.assists },
    { label: 'REB', value: basketball.rebounds },
    { label: 'TOV', value: basketball.turnovers },
    { label: 'STL', value: basketball.steals },
    { label: 'BLK', value: basketball.blocks },
    { label: '3PM', value: basketball.threes_made },
  ] : [
    { label: 'Record', value: team?.record },
    { label: 'Run Diff', value: overall.DIFF },
    { label: 'Runs', value: overall.RS },
    { label: 'Allowed', value: overall.RA },
    { label: 'AVG', value: hitting.avg },
    { label: 'HR', value: hitting.HR },
    { label: 'WHIP', value: pitching.WHIP },
    { label: 'K', value: pitching.SO },
  ];

  return (
    <Card>
      <Box sx={{ color: palette.text, fontSize: '20px', fontWeight: 950, mb: 1 }}>{team?.name}</Box>
      <StatGrid stats={teamStats} />
      <StartingLineupTable lineup={startingLineup} />
      <Box sx={{ mt: 2 }}>
        <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900, mb: 1 }}>W-L Over Time</Box>
        <Box sx={{ height: { xs: 190, sm: 220 }, minHeight: 180 }}>
          <canvas ref={chartRef} />
        </Box>
        <Box sx={{ color: palette.muted, fontSize: '12px', mt: 1 }}>
          Games: {recordTimeline.length.toLocaleString()}
        </Box>
      </Box>
      <LastGamesTable games={lastGames} />
      <TeamLeaders leaders={leaders} />
      <Box sx={{ mt: 1.5, color: palette.muted, fontSize: '12px' }}>As of {dateLabel(card?.as_of)}</Box>
    </Card>
  );
};

const ScoringSummaryCard = ({ summary, league }) => {
  const teams = Array.isArray(summary?.teams) ? summary.teams : [];
  const scoringPlays = Array.isArray(summary?.scoring_plays) ? summary.scoring_plays : [];
  if (!summary?.event_id) return null;
  const isBaseball = league?.sport_key === 'baseball';

  const inningNumbers = [...new Set(teams.flatMap((team) => (
    Array.isArray(team.periods) ? team.periods.map((period) => period.period_number).filter(Boolean) : []
  )))].sort((a, b) => a - b);
  const periodScore = (team, inningNumber) => {
    const period = Array.isArray(team.periods) ? team.periods.find((row) => row.period_number === inningNumber) : null;
    return period?.score ?? '';
  };

  return (
    <Card sx={{ gridColumn: { lg: 'span 2' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Box sx={{ color: palette.text, fontSize: '18px', fontWeight: 950 }}>{summary.label || 'Current matchup'}</Box>
        </Box>
        <Box sx={{ color: palette.muted, fontSize: '12px', fontWeight: 750 }}>
          {summary.completed ? 'Final' : 'Current / Next'} · {dateLabel(summary.starts_at)}
        </Box>
      </Box>

      <Box sx={{ ...scrollAreaSx, border: `1px solid ${palette.border}`, borderRadius: '8px', backgroundColor: palette.surface }}>
        <Box component="table" sx={{ width: '100%', minWidth: { xs: 360, sm: 420 }, borderCollapse: 'collapse', tableLayout: 'auto' }}>
          <Box component="thead">
            <Box component="tr" sx={{ borderBottom: `1px solid ${palette.border}` }}>
              <Box component="th" sx={{ color: palette.muted, textAlign: 'left', py: 0.45, pl: 0.65, pr: 0.35, width: 68, fontSize: '11px', fontWeight: 850 }}>Team</Box>
              {inningNumbers.map((inningNumber) => (
                <Box key={inningNumber} component="th" sx={{ color: palette.muted, textAlign: 'center', py: 0.45, px: 0.35, fontSize: '11px', fontWeight: 850 }}>{inningNumber}</Box>
              ))}
              {(isBaseball ? ['R', 'H', 'E'] : ['Total']).map((header) => (
                <Box key={header} component="th" sx={{ color: palette.text, textAlign: 'center', py: 0.45, px: 0.45, fontSize: '11px', fontWeight: 950 }}>{header}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {teams.map((team) => (
              <Box key={`${team.team_id}-${team.home_away}`} component="tr" sx={{ borderBottom: `1px solid ${palette.border}`, '&:last-child': { borderBottom: 0 } }}>
                <Box component="td" sx={{ py: 0.45, pl: 0.65, pr: 0.35 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45, minWidth: 0 }}>
                    {team.logo_url && <Box component="img" src={team.logo_url} alt="" sx={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }} />}
                    <Box sx={{ color: palette.text, fontSize: '12px', fontWeight: 900 }}>{team.abbreviation || team.team}</Box>
                  </Box>
                </Box>
                {inningNumbers.map((inningNumber) => {
                  const value = periodScore(team, inningNumber);
                  return (
                    <Box
                      key={inningNumber}
                      component="td"
                      sx={{
                        color: value ? palette.text : palette.muted,
                        backgroundColor: Number(value) > 0 ? (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
                        py: 0.45,
                        px: 0.35,
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: Number(value) > 0 ? 900 : 700,
                      }}
                    >
                      {value}
                    </Box>
                  );
                })}
                <Box component="td" sx={{ color: palette.text, py: 0.45, px: 0.45, textAlign: 'center', fontSize: '13px', fontWeight: 950 }}>{statValue(team.runs)}</Box>
                {isBaseball && (
                  <>
                    <Box component="td" sx={{ color: palette.text, py: 0.45, px: 0.45, textAlign: 'center', fontSize: '13px', fontWeight: 900 }}>{statValue(team.hits)}</Box>
                    <Box component="td" sx={{ color: palette.text, py: 0.45, px: 0.45, textAlign: 'center', fontSize: '13px', fontWeight: 900 }}>{statValue(team.errors)}</Box>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ color: palette.text, fontSize: '14px', fontWeight: 900, mb: 1 }}>Scoring Plays</Box>
        {scoringPlays.length === 0 ? (
          <Box sx={{ color: palette.muted, fontSize: '12px', border: `1px dashed ${palette.border}`, borderRadius: '8px', p: 1.2 }}>
            No scoring-play details have been imported for this game yet.
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {scoringPlays.map((play) => (
              <Box
                key={play.id || play.sequence_number}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '92px 1fr' },
                  gap: 1,
                  border: `1px solid ${palette.border}`,
                  borderRadius: '8px',
                  backgroundColor: palette.surface,
                  p: 1,
                }}
              >
                <Box sx={{ color: palette.muted, fontSize: '11px', fontWeight: 850, textTransform: 'uppercase' }}>{play.inning || 'Inning'}</Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
                    {play.logo_url && <Box component="img" src={play.logo_url} alt="" sx={{ width: 18, height: 18, objectFit: 'contain' }} />}
                    <Box sx={{ color: palette.text, fontSize: '12px', fontWeight: 950 }}>{play.abbreviation || play.team || 'Team'}</Box>
                    {play.result && <Box sx={{ color: palette.muted, fontSize: '11px', fontWeight: 800 }}>{play.result}</Box>}
                  </Box>
                  <Box sx={{ color: palette.text, fontSize: '13px', fontWeight: 750, lineHeight: 1.35 }}>{play.description}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
};

const ProbablePitchersTable = ({ team, opponent, probablePitchers }) => {
  const selectedPitcher = probablePitchers?.selected;
  const opponentPitcher = probablePitchers?.opponent;
  const rows = [
    { label: 'W-L', key: 'record', type: 'record' },
    { label: 'ERA', key: 'era', direction: 'lower' },
    { label: 'WHIP', key: 'whip', direction: 'lower' },
    { label: 'IP', key: 'ip' },
    { label: 'H', key: 'h', direction: 'lower' },
    { label: 'K', key: 'k' },
    { label: 'BB', key: 'bb', direction: 'lower' },
    { label: 'HR', key: 'hr', direction: 'lower' },
  ];

  const pitcherHeader = (pitcher, pitcherTeam, align = 'left') => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
      {pitcher?.headshot_url && (
        <Box component="img" src={pitcher.headshot_url} alt="" sx={{ display: { xs: 'none', sm: 'block' }, width: 42, height: 42, borderRadius: '8px', objectFit: 'cover', backgroundColor: palette.panel, flexShrink: 0 }} />
      )}
      <Box sx={{ minWidth: 0, textAlign: align === 'right' ? 'right' : 'left' }}>
        <Box sx={{ color: palette.text, fontSize: { xs: '13px', sm: '15px' }, fontWeight: 950, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pitcher?.name || 'TBD'}</Box>
        <Box sx={{ color: palette.muted, fontSize: '13px', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pitcherTeam?.abbreviation || 'TBD'} · Throws {pitcher?.throws || 'N/A'}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ border: `1px solid ${palette.border}`, borderRadius: '8px', overflow: 'hidden', backgroundColor: palette.surface }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) 64px minmax(0, 1fr)', sm: 'minmax(0, 1fr) 82px minmax(0, 1fr)' }, gap: { xs: 0.6, sm: 1 }, alignItems: 'center', height: { xs: 56, sm: 64 }, boxSizing: 'border-box', p: { xs: 0.75, sm: 1 }, borderBottom: `1px solid ${palette.border}` }}>
        {pitcherHeader(selectedPitcher, team, 'right')}
        <Box aria-hidden="true" />
        {pitcherHeader(opponentPitcher, opponent)}
      </Box>
      {rows.map(({ label, key, direction = 'higher', type = 'number' }) => {
        const selectedValue = selectedPitcher?.stats?.[key];
        const opponentValue = opponentPitcher?.stats?.[key];
        const selectedSide = comparisonSide(selectedValue, opponentValue, direction, type);
        const opponentSide = comparisonSide(opponentValue, selectedValue, direction, type);
        return (
          <Box
            key={key}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) 64px minmax(0, 1fr)', sm: 'minmax(0, 1fr) 82px minmax(0, 1fr)' },
              gap: { xs: 0.6, sm: 1 },
              alignItems: 'center',
              height: { xs: 40, sm: 42 },
              boxSizing: 'border-box',
              px: { xs: 0.75, sm: 1 },
              borderBottom: `1px solid ${palette.border}`,
              '&:last-child': { borderBottom: 0 },
            }}
          >
            <ComparisonValue value={selectedValue} side={selectedSide} align="right" />
            <Box sx={{ color: palette.muted, fontSize: '13px', fontWeight: 850, textAlign: 'center', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{label}</Box>
            <ComparisonValue value={opponentValue} side={opponentSide} />
          </Box>
        );
      })}
    </Box>
  );
};

const MatchupTeamIdentity = ({ team, record, align = 'left' }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: 'center',
      justifyContent: { xs: 'center', sm: align === 'right' ? 'flex-end' : 'flex-start' },
      gap: { xs: 0.35, sm: 1 },
      minWidth: 0,
    }}
  >
    <TeamLogo team={team} size={{ xs: 34, sm: 54 }} />
    <Box sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
      <Box sx={{ color: palette.text, fontSize: { xs: '19px', sm: '24px' }, fontWeight: 950, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {team?.abbreviation || 'TBD'}
      </Box>
      <Box sx={{ color: palette.muted, fontSize: { xs: '13px', sm: '14px' }, mt: { xs: 0.2, sm: 0.35 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record || team?.record || 'N/A'}</Box>
    </Box>
  </Box>
);

const TableTeamHeader = ({ team, record, align = 'left' }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      gap: { xs: 0.5, sm: 0.75 },
      minWidth: 0,
    }}
  >
    <TeamLogo team={team} size={{ xs: 26, sm: 32 }} />
    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
      <Box sx={{ color: palette.text, fontSize: { xs: '14px', sm: '15px' }, fontWeight: 950, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {team?.abbreviation || 'TBD'}
      </Box>
      <Box sx={{ color: palette.muted, fontSize: '12.5px', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record || team?.record || 'N/A'}</Box>
    </Box>
  </Box>
);

const normalizedTeamToken = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const teamMatchesToken = (team, token) => {
  const normalizedToken = normalizedTeamToken(token);
  if (!normalizedToken) return false;

  return [
    team?.abbreviation,
    team?.short_name,
    team?.name,
  ].some((value) => normalizedTeamToken(value) === normalizedToken);
};

const matchupLabelOrder = (label, entries) => {
  const match = String(label || '').match(/^\s*([^@]+?)\s*@\s*([^@]+?)\s*$/);
  if (!match) return null;

  const awayEntry = entries.find((entry) => teamMatchesToken(entry.team, match[1]));
  const homeEntry = entries.find((entry) => teamMatchesToken(entry.team, match[2]));
  if (!awayEntry || !homeEntry || awayEntry === homeEntry) return null;

  return [awayEntry, homeEntry];
};

const TeamVsTeamCard = ({ team, matchup, league }) => {
  const sportKey = league?.sport_key;
  const opponent = matchup?.opponent;
  const probablePitchers = matchup?.probable_pitchers || {};
  const selectedStats = matchup?.selected_stats || {};
  const opponentStats = matchup?.opponent_stats || {};
  const selectedEntry = {
    team,
    record: team?.record || selectedStats.record,
    stats: selectedStats,
    pitcher: probablePitchers.selected,
    score: matchup?.selected_score,
  };
  const opponentEntry = {
    team: opponent,
    record: matchup?.opponent_record || opponent?.record || opponentStats.record,
    stats: opponentStats,
    pitcher: probablePitchers.opponent,
    score: matchup?.opponent_score,
  };
  const entries = [selectedEntry, opponentEntry];
  const defaultOrder = matchup?.selected_side === 'home' ? [opponentEntry, selectedEntry] : [selectedEntry, opponentEntry];
  const [leftEntry, rightEntry] = matchupLabelOrder(matchup?.label, entries) || defaultOrder;
  const leftTeam = leftEntry.team;
  const rightTeam = rightEntry.team;
  const leftRecord = leftEntry.record;
  const rightRecord = rightEntry.record;
  const leftStats = leftEntry.stats || {};
  const rightStats = rightEntry.stats || {};
  const displayProbablePitchers = {
    selected: leftEntry.pitcher,
    opponent: rightEntry.pitcher,
  };
  const comparisonRows = sportKey === 'basketball' ? [
    { label: 'Record', selectedValue: leftStats.record, opponentValue: rightStats.record, type: 'record' },
    { label: 'Diff', selectedValue: leftStats.point_diff, opponentValue: rightStats.point_diff },
    { label: 'PPG', selectedValue: leftStats.ppg, opponentValue: rightStats.ppg },
    { label: 'Opp PPG', selectedValue: leftStats.opponent_ppg, opponentValue: rightStats.opponent_ppg, direction: 'lower' },
    { label: 'AST', selectedValue: leftStats.assists, opponentValue: rightStats.assists },
    { label: 'REB', selectedValue: leftStats.rebounds, opponentValue: rightStats.rebounds },
    { label: 'TOV', selectedValue: leftStats.turnovers, opponentValue: rightStats.turnovers, direction: 'lower' },
    { label: 'STL', selectedValue: leftStats.steals, opponentValue: rightStats.steals },
    { label: 'BLK', selectedValue: leftStats.blocks, opponentValue: rightStats.blocks },
  ] : [
    { label: 'Record', selectedValue: leftStats.record, opponentValue: rightStats.record, type: 'record' },
    { label: 'Run Diff', selectedValue: leftStats.run_diff, opponentValue: rightStats.run_diff },
    { label: 'Runs', selectedValue: leftStats.runs_scored, opponentValue: rightStats.runs_scored },
    { label: 'Allowed', selectedValue: leftStats.runs_allowed, opponentValue: rightStats.runs_allowed, direction: 'lower' },
    { label: 'AVG', selectedValue: leftStats.avg, opponentValue: rightStats.avg },
    { label: 'HR', selectedValue: leftStats.home_runs, opponentValue: rightStats.home_runs },
    { label: 'WHIP', selectedValue: leftStats.whip, opponentValue: rightStats.whip, direction: 'lower' },
    { label: 'K', selectedValue: leftStats.strikeouts, opponentValue: rightStats.strikeouts },
  ];

  return (
    <Card>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(112px, auto) minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(190px, auto) minmax(0, 1fr)' },
          gap: { xs: 0.75, sm: 1.5 },
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <MatchupTeamIdentity team={leftTeam} record={leftRecord} align="right" />
        <Box sx={{ textAlign: 'center', minWidth: 0 }}>
          <Box sx={{ color: palette.text, fontSize: { xs: '21px', sm: '26px' }, fontWeight: 950, lineHeight: 1.05, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {matchup?.label || 'No matchup found'}
          </Box>
          <Box sx={{ color: palette.muted, fontSize: { xs: '13px', sm: '14px' }, mt: 0.55 }}>{dateLabel(matchup?.starts_at)}</Box>
          {matchup?.completed && (
            <Box sx={{ color: palette.text, fontSize: '26px', fontWeight: 950, mt: 0.75 }}>
              {leftEntry.score} - {rightEntry.score}
            </Box>
          )}
        </Box>
        <MatchupTeamIdentity team={rightTeam} record={rightRecord} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: sportKey !== 'basketball' ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr' },
          gap: { xs: 2, md: 2.5 },
          alignItems: 'start',
          mt: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ color: palette.text, fontSize: '16px', fontWeight: 900, mb: 1 }}>Team Stats</Box>
          <Box sx={{ border: `1px solid ${palette.border}`, borderRadius: '8px', overflow: 'hidden', backgroundColor: palette.surface }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) 74px minmax(0, 1fr)', sm: 'minmax(0, 1fr) 90px minmax(0, 1fr)' }, gap: { xs: 0.6, sm: 1 }, alignItems: 'center', height: { xs: 56, sm: 64 }, boxSizing: 'border-box', px: { xs: 0.75, sm: 1 }, py: 0.65, borderBottom: `1px solid ${palette.border}` }}>
              <TableTeamHeader team={leftTeam} record={leftRecord} align="right" />
              <Box aria-hidden="true" />
              <TableTeamHeader team={rightTeam} record={rightRecord} />
            </Box>
            {comparisonRows.map(({ label, selectedValue, opponentValue, direction = 'higher', type = 'number' }) => {
              const selectedSide = comparisonSide(selectedValue, opponentValue, direction, type);
              const opponentSide = comparisonSide(opponentValue, selectedValue, direction, type);
              return (
                <Box
                  key={label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr) 74px minmax(0, 1fr)', sm: 'minmax(0, 1fr) 90px minmax(0, 1fr)' },
                    gap: { xs: 0.6, sm: 1 },
                    alignItems: 'center',
                    height: { xs: 40, sm: 42 },
                    boxSizing: 'border-box',
                    px: { xs: 0.75, sm: 1 },
                    borderBottom: `1px solid ${palette.border}`,
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ComparisonValue value={selectedValue} side={selectedSide} align="right" />
                  <Box sx={{ color: palette.muted, fontSize: '13px', fontWeight: 850, textAlign: 'center', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{label}</Box>
                  <ComparisonValue value={opponentValue} side={opponentSide} />
                </Box>
              );
            })}
          </Box>
        </Box>
        {sportKey !== 'basketball' && (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ color: palette.text, fontSize: '16px', fontWeight: 900, mb: 1 }}>Probable Pitchers</Box>
            <ProbablePitchersTable team={leftTeam} opponent={rightTeam} probablePitchers={displayProbablePitchers} />
          </Box>
        )}
      </Box>
    </Card>
  );
};

export function SportsDataOverview() {
  const [league, setLeague] = useState('MLB');
  const [teamId, setTeamId] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gqlFetch(SPORTS_DATA_CARDS_QUERY, {
        league,
        teamId: teamId || null,
        playerId: playerId || null,
      });
      setData(response?.admin?.getSportsDataCards || null);
    } catch (err) {
      console.error('Sports data cards fetch failed', err);
      setError(err.message || 'Unable to load sports data cards.');
    } finally {
      setLoading(false);
    }
  }, [league, playerId, teamId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const teams = Array.isArray(data?.teams) ? data.teams : [];
  const players = Array.isArray(data?.players) ? data.players : [];
  const selectedTeam = data?.selected_team;
  const selectedPlayer = data?.selected_player;
  const selectedLeague = data?.league;
  const selectedTeamValue = teamId || selectedTeam?.id || '';
  const selectedPlayerValue = playerId || selectedPlayer?.id || '';

  const teamOptions = useMemo(() => teams.map((team) => (
    <MenuItem key={team.id} value={String(team.id)}>{team.abbreviation} · {team.name}</MenuItem>
  )), [teams]);

  const playerOptions = useMemo(() => players.map((player) => (
    <MenuItem key={player.id} value={String(player.id)}>{player.name} · {player.position || 'N/A'}</MenuItem>
  )), [players]);

  return (
    <Box sx={{ color: palette.text, display: 'flex', flexDirection: 'column', gap: { xs: 1.4, sm: 2 }, pb: 4, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: { xs: '22px', sm: '24px' }, fontWeight: 850, lineHeight: 1.2 }}>Sports Data</Box>
          <Box sx={{ color: palette.muted, fontSize: '13px', mt: 0.5 }}>{league} team, player, and matchup cards from local sports data.</Box>
        </Box>
        <Button variant="contained" onClick={loadData} disabled={loading} sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}>{loading ? 'Loading' : 'Refresh'}</Button>
      </Box>

      <Card sx={{ backgroundColor: palette.surface }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px minmax(220px, 1fr) minmax(240px, 1fr)' }, gap: 1.5 }}>
          <TextField
            select
            label="League"
            value={league}
            onChange={(event) => {
              setLeague(event.target.value);
              setTeamId('');
              setPlayerId('');
            }}
            size="small"
            sx={{ ...textFieldStyles, width: '100%', minWidth: 0 }}
          >
            {SUPPORTED_LEAGUES.map((leagueOption) => (
              <MenuItem key={leagueOption} value={leagueOption}>{leagueOption}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Team"
            value={String(selectedTeamValue)}
            onChange={(event) => {
              setTeamId(event.target.value);
              setPlayerId('');
            }}
            size="small"
            sx={{ ...textFieldStyles, width: '100%', minWidth: 0 }}
          >
            {teamOptions}
          </TextField>
          <TextField
            select
            label="Player"
            value={String(selectedPlayerValue)}
            onChange={(event) => setPlayerId(event.target.value)}
            size="small"
            sx={{ ...textFieldStyles, width: '100%', minWidth: 0 }}
          >
            {playerOptions}
          </TextField>
        </Box>
      </Card>

      {error && (
        <Box sx={{ color: '#ff8a80', border: '1px solid #ff8a80', borderRadius: '8px', p: 1.5 }}>{error}</Box>
      )}

      <TeamHeader team={selectedTeam} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <PlayerCard player={selectedPlayer} card={data?.player_card} team={selectedTeam} />
        <TeamCard team={selectedTeam} card={data?.team_card} />
        <TeamVsTeamCard team={selectedTeam} matchup={data?.matchup_card} league={selectedLeague} />
        <ScoringSummaryCard summary={data?.scoring_summary} league={selectedLeague} />
      </Box>
    </Box>
  );
}

export { PlayerCard, TeamCard, TeamVsTeamCard, ScoringSummaryCard };

export default SportsDataOverview;
